// Plans de contrôle : période, objets ciblés, responsable, ordres rattachés ; création par le responsable.
import { CODES_OBJETS_ORDRE, OBJETS_ORDRE_MISSION, schemaCreationPlanControle } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, Dialogue, EtatVide, LienBouton, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, CalendarRange, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { aujourdhuiIso } from '../proprete/logique';
import { creerPlan, INVALIDER_INSPECTIONS, usePlans } from './api';
import { libelleObjet } from './logique';
import type { ObjetOrdreMission, Plan } from './types';
import styles from '../proprete/proprete.module.css';
import commun from '../../commun/commun.module.css';

function FormulairePlan({ onFermer }: { onFermer: () => void }) {
  const [v, setV] = useState({ nom: '', debut: aujourdhuiIso(), fin: aujourdhuiIso(), objets: [] as ObjetOrdreMission[] });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationPlanControle.safeParse({ nom: v.nom, periode_debut: v.debut, periode_fin: v.fin, objets_cibles: v.objets.map((o) => ({ objet_type: o, libelle: OBJETS_ORDRE_MISSION[o] })) });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerPlan(corps.data);
    },
    succes: (p) => `Plan ${p.nom} créé, code ${p.code}.`,
    invalider: INVALIDER_INSPECTIONS,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue ouvert titre="Nouveau plan de contrôle" onFermer={onFermer} actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer le plan</Bouton></>}>
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV((x) => ({ ...x, nom: e.target.value }))} erreur={champ('nom')} required />
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Début" type="date" value={v.debut} onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))} erreur={champ('periode_debut')} required />
        <Champ libelle="Fin" type="date" value={v.fin} onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))} erreur={champ('periode_fin')} required />
      </div>
      <fieldset>
        <legend className="cc-etiquette">Objets ciblés</legend>
        <div className={styles.reponses}>
          {CODES_OBJETS_ORDRE.map((o) => <CaseACocher key={o} libelle={OBJETS_ORDRE_MISSION[o]} checked={v.objets.includes(o)} onChange={() => setV((x) => ({ ...x, objets: x.objets.includes(o) ? x.objets.filter((y) => y !== o) : [...x.objets, o] }))} />)}
        </div>
      </fieldset>
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PagePlans() {
  const { contexte } = useSession();
  const plans = usePlans();
  const [creation, setCreation] = useState(false);
  const colonnes: ColonneTableau<Plan>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (p) => <span className={commun.reference}>{p.code}</span>, valeurTri: (p) => p.code },
    { id: 'nom', entete: 'Plan', cellule: (p) => p.nom, valeurTri: (p) => p.nom },
    { id: 'periode', entete: 'Période', cellule: (p) => `${date(p.periode.debut)} au ${date(p.periode.fin)}`, valeurTri: (p) => p.periode.debut },
    { id: 'cibles', entete: 'Objets ciblés', cellule: (p) => (p.objets_cibles.length === 0 ? 'non précisés' : p.objets_cibles.map((c) => c.libelle ?? libelleObjet(c.objet_type)).join(', ')) },
    { id: 'responsable', entete: 'Responsable', cellule: (p) => p.responsable?.nom ?? 'non désigné' },
    { id: 'ordres', entete: 'Ordres (clos)', cellule: (p) => `${p.statistiques?.ordres ?? 0} (${p.statistiques?.clos ?? 0})` },
  ];
  return (
    <>
      <EnTetePage etiquette="Inspections et contrôles" titre="Plans de contrôle" description="Programmes de contrôle par période et objets ciblés ; les ordres de mission s'y rattachent."
        actions={<><LienBouton href="/inspections" icone={ArrowLeft} variante="secondaire">Ordres</LienBouton>{peut(contexte, 'inspection', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouveau plan</Bouton> : null}</>} />
      <EtatRequete chargement={plans.isPending} erreur={plans.error} onReessayer={() => void plans.refetch()} lot="L3-B" fonction="La liste des plans">
        {(plans.data ?? []).length === 0 ? <EtatVide titre="Aucun plan de contrôle" icone={CalendarRange}><p>Aucun plan n&apos;est défini ; les ordres peuvent être émis hors plan.</p></EtatVide> : <Tableau legende="Plans de contrôle" colonnes={colonnes} lignes={plans.data ?? []} cleLigne={(p) => p.id} cleColonnes="inspections-plans" />}
      </EtatRequete>
      {creation ? <FormulairePlan onFermer={() => setCreation(false)} /> : null}
    </>
  );
}
